import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Typography,
  Row,
  Col,
  Statistic,
  Switch,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import {
  FireOutlined,
  PlusOutlined,
  EditOutlined,
  BellOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import {
  heatingApi,
  HeatingSeason,
  HeatingSeasonCreatePayload,
} from '../../api/heating';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface FormValues {
  season_name: string;
  range: [Dayjs, Dayjs];
  school_closure_temp: number;
  notes?: string;
  is_active?: boolean;
}

const HeatingPage: React.FC = () => {
  const [seasons, setSeasons] = useState<HeatingSeason[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await heatingApi.list();
      setSeasons(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить сезоны');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    form.resetFields();
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (season: HeatingSeason) => {
    form.setFieldsValue({
      season_name: season.season_name,
      range: [dayjs(season.start_date), dayjs(season.end_date)],
      school_closure_temp: season.school_closure_temp,
      notes: season.notes || undefined,
      is_active: season.is_active,
    });
    setEditingId(season.id);
    setOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload: HeatingSeasonCreatePayload & { is_active?: boolean } = {
        season_name: v.season_name,
        start_date: v.range[0].format('YYYY-MM-DD'),
        end_date: v.range[1].format('YYYY-MM-DD'),
        school_closure_temp: v.school_closure_temp,
        notes: v.notes,
      };
      if (editingId) {
        await heatingApi.update(editingId, { ...payload, is_active: v.is_active });
        message.success('Сезон обновлён');
      } else {
        await heatingApi.create(payload);
        message.success('Сезон создан');
      }
      setOpen(false);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const sendSchoolPush = async () => {
    try {
      const r = await heatingApi.sendSchoolClosurePush();
      message.success(r.message || `Push отправлен (${r.sent})`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось отправить push');
    }
  };

  const active = seasons.find((s) => s.is_heating_now);

  const columns: ColumnsType<HeatingSeason> = [
    { title: 'Сезон', dataIndex: 'season_name', key: 'season_name',
      render: (v: string, r) => (
        <Space>
          <FireOutlined style={{ color: r.is_heating_now ? 'var(--l-danger)' : 'var(--l-faint)' }} />
          <Text strong>{v}</Text>
          {r.is_heating_now && <Tag color="green">идёт сейчас</Tag>}
          {!r.is_active && <Tag>архив</Tag>}
        </Space>
      ),
    },
    {
      title: 'Период',
      key: 'period',
      render: (_v, r) => (
        <Text type="secondary">
          {dayjs(r.start_date).format('DD.MM.YYYY')} → {dayjs(r.end_date).format('DD.MM.YYYY')}
        </Text>
      ),
    },
    {
      title: 'T° закрытия школ',
      dataIndex: 'school_closure_temp',
      key: 'school_closure_temp',
      align: 'center' as const,
      render: (t: number) => <Tag>{t} °C</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_v, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Изменить
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Текущий сезон"
              value={active?.season_name || '—'}
              prefix={<FireOutlined style={{ color: active ? 'var(--l-danger)' : 'var(--l-faint)' }} />}
            />
            {active && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Осталось {active.days_remaining} дн.
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="T° для отмены занятий"
              value={active?.school_closure_temp ?? '—'}
              suffix="°C"
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">Push об отмене занятий</Text>
              <Popconfirm
                title="Отправить уведомление об отмене занятий?"
                description="Все авторизованные жители получат push"
                onConfirm={sendSchoolPush}
                okText="Отправить"
                cancelText="Отмена"
              >
                <Button type="primary" danger icon={<BellOutlined />} block>
                  Отправить push «школы закрыты»
                </Button>
              </Popconfirm>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card
        title={<Title level={4} style={{ margin: 0 }}>Отопительные сезоны</Title>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Обновить
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Новый сезон
            </Button>
          </Space>
        }
      >
        {!loading && seasons.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="Нет отопительных сезонов"
            description="Создайте сезон 2025-2026 чтобы получать уведомления о морозах и отправлять push об отмене занятий."
            style={{ marginBottom: 16 }}
          />
        )}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={seasons}
          columns={columns}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingId ? 'Изменить сезон' : 'Новый отопительный сезон'}
        open={open}
        onOk={submit}
        onCancel={() => setOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Название сезона"
            name="season_name"
            rules={[{ required: true, message: 'Укажите название' }]}
          >
            <Input placeholder="Например: 2025-2026" />
          </Form.Item>
          <Form.Item
            label="Период"
            name="range"
            rules={[{ required: true, message: 'Укажите период' }]}
          >
            <DatePicker.RangePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            label="T° для отмены занятий (°C)"
            name="school_closure_temp"
            rules={[{ required: true, message: 'Укажите температуру' }]}
            initialValue={-30}
          >
            <InputNumber min={-50} max={0} style={{ width: '100%' }} />
          </Form.Item>
          {editingId && (
            <Form.Item label="Активный" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
          <Form.Item label="Заметки" name="notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HeatingPage;
